import { useEffect, useState, type FormEvent } from "react";
import { CHANNEL_LABELS, type MessageChannel, type OutboxMessage } from "@shared/modules/x-messagerie";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { ApiError } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { atelierEnabled, createMessage, searchClients, type ClientHit } from "../lib/api";

const FORM_ID = "messagerie-nouveau";

interface NewMessageModalProps {
  open: boolean;
  onClose: () => void;
  onSent: (message: OutboxMessage) => void;
}

/**
 * Message libre : un client de l'atelier (recherche par nom ou téléphone) ou
 * une adresse tapée directement ; SMS ou courriel ; envoi immédiat.
 */
export function NewMessageModal({ open, onClose, onSent }: NewMessageModalProps) {
  const push = useToastStore((s) => s.push);
  const [channel, setChannel] = useState<MessageChannel>("sms");
  const [mode, setMode] = useState<"client" | "direct">(atelierEnabled ? "client" : "direct");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<ClientHit[]>([]);
  const [client, setClient] = useState<ClientHit | null>(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChannel("sms");
    setMode(atelierEnabled ? "client" : "direct");
    setQuery("");
    setHits([]);
    setClient(null);
    setTo("");
    setSubject("");
    setBody("");
  }, [open]);

  // Recherche de clients, avec délai pour ne pas interroger à chaque frappe.
  useEffect(() => {
    if (mode !== "client" || client || query.trim().length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchClients(query.trim())
        .then((list) => {
          if (!cancelled) setHits(list.slice(0, 8));
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, mode, client]);

  const clientAddress = client ? (channel === "sms" ? client.phone : client.email) : null;
  const canSend =
    body.trim().length > 0 &&
    !sending &&
    (mode === "client" ? Boolean(client && clientAddress) : to.trim().length > 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    try {
      const sent = await createMessage({
        channel,
        clientId: mode === "client" ? client!.id : undefined,
        to: mode === "direct" ? to.trim() : undefined,
        subject: channel === "email" && subject.trim() ? subject.trim() : undefined,
        body: body.trim(),
      });
      push(
        sent.status === "echec" ? "danger" : "success",
        sent.status === "simule" ? "Message simulé" : sent.status === "echec" ? "Envoi en échec" : "Message envoyé",
        sent.status === "simule" ? "Aucune clé d'envoi configurée : le message est stocké sans être transmis." : sent.toAddress,
      );
      onSent(sent);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push("danger", "Envoi impossible", code === "no_address" ? "Ce client n'a pas d'adresse pour ce canal." : "Vérifiez le destinataire et le message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Nouveau message"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={sending}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={!canSend}>
            {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="nouveau-message" onSubmit={submit}>
        <div className="form-grid">
          <Field label="Canal">
            <Select value={channel} onChange={(e) => setChannel(e.target.value as MessageChannel)}>
              {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          {atelierEnabled ? (
            <Field label="Destinataire">
              <Select value={mode} onChange={(e) => setMode(e.target.value as "client" | "direct")}>
                <option value="client">Un client de l'atelier</option>
                <option value="direct">Une adresse directe</option>
              </Select>
            </Field>
          ) : null}
        </div>

        {mode === "client" ? (
          client ? (
            <div className="nouveau-message__picked">
              <div>
                <div className="nouveau-message__picked-name">{client.name}</div>
                <div className="nouveau-message__picked-meta">
                  {clientAddress ?? (channel === "sms" ? "Aucun téléphone au dossier" : "Aucun courriel au dossier")}
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setClient(null)}>
                Changer
              </Button>
            </div>
          ) : (
            <div className="nouveau-message__search">
              <Field label="Client" hint="Nom, téléphone ou courriel.">
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ex : Tremblay"
                  autoFocus
                />
              </Field>
              {hits.length > 0 ? (
                <ul className="nouveau-message__results" role="listbox" aria-label="Clients trouvés">
                  {hits.map((h) => (
                    <li key={h.id}>
                      <button type="button" className="nouveau-message__result" onClick={() => setClient(h)}>
                        <span className="nouveau-message__result-name">{h.name}</span>
                        <span className="nouveau-message__result-meta">{[h.phone, h.email].filter(Boolean).join(" · ") || "Sans coordonnées"}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )
        ) : (
          <Field label={channel === "sms" ? "Numéro de téléphone" : "Adresse courriel"}>
            <Input
              type={channel === "sms" ? "tel" : "email"}
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={channel === "sms" ? "ex : 418 555-0134" : "ex : client@exemple.com"}
              required
            />
          </Field>
        )}

        {channel === "email" ? (
          <Field label="Objet">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Message de PROTECTO" />
          </Field>
        ) : null}

        <Field
          label="Message"
          hint={mode === "client" ? "{{clientPrenom}} et {{clientNom}} sont remplacés par le nom du client." : undefined}
        >
          <Textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Bonjour {{clientPrenom}}, … — PROTECTO"
            required
          />
        </Field>
      </form>
    </Modal>
  );
}
